/* eslint-disable filenames/match-regex */

import CreatorLiteService from '@/services/CreatorLite.js';

const ITEM_PER_PAGE = 20;

const state = {
  webFontScriptLoaded: false,
  fontsLoaded: false,
  isLoading: false,
  hasMore: true,
  page: 0,
  list: [],
};

const getters = {
  webFontScriptLoaded(state) {
    return state.webFontScriptLoaded;
  },
  fontsLoaded(state) {
    return state.fontsLoaded;
  },
  isLoading(state) {
    return state.isLoading;
  },
  hasMore(state) {
    return state.hasMore;
  },
  page(state) {
    return state.page;
  },
  list(state) {
    return state.list;
  },
  hasAnyItem(state) {
    return state.list.length > 0;
  },
};

const actions = {
  loadWebFontScript(store) {
    return new Promise((resolve, reject) => {
      if (store.getters['webFontScriptLoaded']) {
        resolve();
        return;
      }

      store.commit('setWebFontScriptLoaded', true);

      const scriptEl = document.createElement('script');
      scriptEl.src = 'https://ajax.googleapis.com/ajax/libs/webfont/1.6.26/webfont.js';
      scriptEl.onload = resolve;
      scriptEl.onerror = reject;
      document.head.appendChild(scriptEl);
    });
  },
  async loadAvailableFonts(store) {
    const fontFamilies = store.rootGetters['app/fontFamilies'];
    if (fontFamilies.length === 0) {
      return;
    }

    try {
      const families = fontFamilies.map(font => font.name + ':' + font.variants.join(','));

      await store.dispatch('loadWebFontScript');

      window.WebFont.load({
        google: { families },
        active: () => {
          store.commit('setFontsLoaded', true);
        },
        inactive: () => {
          console.error('Failed to load font:', this.fontFamily);
          this.fontLoaded = false;
          this.quickRender();
        },
      });
    }
    catch (error) {
      console.error('Error fetching Google Fonts list:', error);
    }
  },
  resetView(store) {
    store.commit('setIsLoading', false);
    store.commit('setHasMore', true);
    store.commit('setPage', 0);
    store.commit('setList', []);
  },
  loadMoreData(store) {
    if (
      store.getters['isLoading']
      || !store.getters['hasMore']
    ) {
      return Promise.resolve(false);
    }

    store.commit('setIsLoading', true);
    store.commit('increasePage');

    const params = {
      term: store.getters['searchTerm'],
      page: store.getters['page'],
      limit: ITEM_PER_PAGE,
    };

    return CreatorLiteService.getList(params)
      .then(resp => {
        const list = store.getters['list'];
        resp.forEach(item => list.push(item));

        store.commit('setHasMore', resp.length === ITEM_PER_PAGE);

        return resp;
      })
      .catch(err => {
        store.commit('setHasMore', false);
        throw err;
      })
      .finally(() => store.commit('setIsLoading', false));
  },
  addToList(store, item) {
    const list = store.getters['list'];
    if (list.length !== 0 && list.length % ITEM_PER_PAGE === 0) {
      store.commit('setHasMore', true);
      list.pop();
    }

    list.unshift(item);
    store.commit('setList', list);
  },
  updateItem(store, item) {
    const list = store.getters['list'];
    const index = list.findIndex(i => i.id === item.id);
    if (index !== -1) {
      list.splice(index, 1, item);
      store.commit('setList', list);
    }
  },
};

const mutations = {
  setWebFontScriptLoaded(state, value) {
    state.webFontScriptLoaded = value;
  },
  setFontsLoaded(state, value) {
    state.fontsLoaded = value;
  },
  setIsLoading(state, value) {
    state.isLoading = value;
  },
  setHasMore(state, value) {
    state.hasMore = value;
  },
  setPage(state, value) {
    state.page = value;
  },
  increasePage(state) {
    state.page += 1;
  },
  setList(state, value) {
    state.list = value;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
